import React, { useEffect } from 'react';
import { Table } from 'reactstrap';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { Award, GraduationCap, ArrowRight } from 'lucide-react';
import { optionalCoursesList } from '../../ProData';

export default function BThContent() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="vj-academic-page py-5">
      <Helmet>
        <title>Bachelor of Theology (B.Th.) - Vidyajyoti College of Theology</title>
        <meta name="description" content="Bachelor of Theology (B.Th.) degree curriculum, structure, syllabus, and examination details at Vidyajyoti College of Theology." />
      </Helmet>

      <div className="container">
        {/* Header */}
        <div className="text-center mx-auto pb-5" style={{ maxWidth: '800px' }}>
          <span className="text-burgundy fw-bold text-uppercase small" style={{ letterSpacing: '0.1em' }}>
            FIRST CYCLE DEGREE PROGRAM
          </span>
          <h1 className="vj-card-title display-6 fw-bold mt-1 text-dark-main">
            Bachelor of Theology (B.Th.)
          </h1>
          <p className="text-muted mt-2">
            A comprehensive 3-year ecclesiastical curriculum combining rigorous academic research with contextual praxis and social pastoral engagement.
          </p>
          <div className="mx-auto mt-2" style={{ width: '60px', height: '3px', background: '#79031d', borderRadius: '2px' }} />
        </div>

        {/* Overview Banner Card */}
        <div className="vj-modern-card p-4 p-md-5 mb-5" style={{ borderLeft: '5px solid #1a2b56' }}>
          <div className="d-flex align-items-center gap-2 mb-3">
            <div className="vj-icon-badge">
              <GraduationCap size={22} />
            </div>
            <h3 className="fw-bold fs-4 text-dark mb-0">Program Overview & Credit Structure</h3>
          </div>
          <p className="text-muted" style={{ lineHeight: '1.8' }}>
            The first cycle which leads to the <strong>B.Th. degree</strong> is organized based on credits. A credit represents approximately 15 lecture periods with corresponding personal research and tutorial guidance. A total of about <strong>107 credits</strong> is required to complete the program.
          </p>
          <div className="d-flex flex-wrap gap-2 pt-2">
            <span className="badge bg-primary-subtle text-primary px-3 py-2 rounded-pill fw-semibold">
              3 Years Duration
            </span>
            <span className="badge bg-danger-subtle text-danger px-3 py-2 rounded-pill fw-semibold">
              107 Total Credits
            </span>
            <span className="badge bg-warning-subtle text-dark px-3 py-2 rounded-pill fw-semibold">
              Contextual Theological Praxis
            </span>
          </div>
        </div>

        {/* Three Tier Theme Structure */}
        <div className="mb-5">
          <h3 className="fw-bold fs-4 text-dark-main text-center mb-4">Three-Tier Holistic Curriculum</h3>
          <div className="row g-4">
            {/* Year 1 */}
            <div className="col-lg-4 d-flex">
              <div className="vj-modern-card p-4 w-100">
                <span className="badge bg-dark-main px-3 py-1 rounded-pill small mb-3">Year 1 • Theme 1</span>
                <h4 className="fw-bold fs-5 text-dark mb-2">God Within History & Faith Responses</h4>
                <p className="text-muted small mb-0" style={{ lineHeight: '1.7' }}>
                  Critical socio-cultural analysis situating human quests for meaning, the plight of marginalized communities (tribals, Dalits, women), and divine revelation across world religious traditions.
                </p>
              </div>
            </div>

            {/* Year 2 */}
            <div className="col-lg-4 d-flex">
              <div className="vj-modern-card p-4 w-100">
                <span className="badge bg-dark-sub px-3 py-1 rounded-pill small mb-3">Year 2 • Theme 2</span>
                <h4 className="fw-bold fs-5 text-dark mb-2">God in Jesus Christ & Sacraments</h4>
                <p className="text-muted small mb-0" style={{ lineHeight: '1.7' }}>
                  Diving into the mystery of Christ, the universal mission of Jesus with preferential option for the poor, the nature of the Church as servant, and sacramental mediation.
                </p>
              </div>
            </div>

            {/* Year 3 */}
            <div className="col-lg-4 d-flex">
              <div className="vj-modern-card p-4 w-100">
                <span className="badge bg-dark-main px-3 py-1 rounded-pill small mb-3">Year 3 • Theme 3</span>
                <h4 className="fw-bold fs-5 text-dark mb-2">Christian Life & Mission in the World</h4>
                <p className="text-muted small mb-0" style={{ lineHeight: '1.7' }}>
                  Reflecting upon ethical questions, global concerns, practical pastoral knowledge, and spirituality to engage modern societal issues with life-affirming hope.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Lectures & Comprehensive Exams */}
        <div className="vj-modern-card p-4 p-md-5 mb-5">
          <div className="d-flex align-items-center gap-2 mb-3">
            <div className="vj-icon-badge burgundy">
              <Award size={22} />
            </div>
            <h3 className="fw-bold fs-4 text-dark mb-0">Lectures & Comprehensive Examinations</h3>
          </div>
          <p className="text-muted" style={{ lineHeight: '1.8' }}>
            Regular attendance at lectures is expected. The final B.Th. comprehensive examination consists of a 60-minute oral viva-voce before a board of three faculty examiners or an approved comprehensive research paper written under faculty supervision.
          </p>
        </div>

        {/* Optional Courses Table */}
        <div className="vj-modern-card p-4 p-md-5 mb-5">
          <div className="d-flex align-items-center justify-content-between mb-4 flex-wrap gap-2">
            <div>
              <h3 className="fw-bold fs-4 text-dark mb-1">Elective & Optional Courses</h3>
              <p className="text-muted small mb-0">Courses currently offered across theological disciplines</p>
            </div>
            <Link to="/admission/application-form" className="vj-btn-primary">
              Apply for B.Th. <ArrowRight size={15} />
            </Link>
          </div>

          <div className="table-responsive rounded-3 border">
            <Table hover className="mb-0">
              <thead className="table-light">
                <tr>
                  <th style={{ width: '80px' }}>#</th>
                  <th>Course Title</th>
                  <th style={{ width: '120px' }}>Credits</th>
                </tr>
              </thead>
              <tbody>
                {optionalCoursesList.map((item, index) => (
                  <tr key={index}>
                    <th scope="row" className="text-muted">{index + 1}</th>
                    <td className="fw-semibold text-dark">{item.course}</td>
                    <td>
                      <span className="badge bg-secondary-subtle text-dark px-2 py-1 rounded">
                        {item.credits} Credit{item.credits > 1 ? 's' : ''}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </div>
      </div>
    </div>
  );
}
